import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useInviteOwedBreakdown } from '@/hooks/useInviteOwedBreakdown';
import { formatMoney } from '@/lib/money';
import { paymentSourceLabel } from '@/lib/moneyPages';
import type { BreakdownComponent, BreakdownKind } from '@/api/types';

/**
 * SPA-1208 + transparency. Collapsible explanation of why the Money card's
 * numbers are what they are. Fetches only when the inviter actually opens
 * it (enabled: open) so paid-in-full invites don't pay for the extra
 * round-trip.
 *
 * Two auto-expanded panels when relevant:
 *   - "Why is this $0?" — only renders when locked > 0, paid < locked,
 *     and owed === 0. The headline sentence an inviter asking the
 *     question would recognise.
 *   - "Payment history" — chronological cash events (PAYOUT, RECONCILE,
 *     REVERSAL) with running balance.
 */

interface Props {
  publicId: string;
  lockedValue: string;
  amountPaid: string;
  stillOwed: number;
}

const CASH_KINDS: BreakdownKind[] = ['PAYOUT', 'RECONCILE', 'REVERSAL'];

function fmtSigned(amount: string): string {
  if (amount.startsWith('-')) return `−$${formatMoney(amount.slice(1))}`;
  return `+$${formatMoney(amount)}`;
}

function formatDay(iso: string): string {
  return iso.slice(0, 10);
}

function KindBadge({ kind }: { kind: BreakdownKind }): JSX.Element {
  const tone =
    kind === 'PAYOUT'
      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
      : kind === 'RECONCILE'
      ? 'bg-sky-500/10 text-sky-700 dark:text-sky-300'
      : kind === 'REVERSAL'
      ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
      : kind === 'REJECT'
      ? 'bg-rose-500/10 text-rose-700 dark:text-rose-300'
      : kind === 'SETTLEMENT'
      ? 'bg-violet-500/10 text-violet-700 dark:text-violet-300'
      : 'bg-muted text-muted-foreground';
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}>
      {paymentSourceLabel(kind)}
    </span>
  );
}

export function OwedBreakdownPanel({
  publicId,
  lockedValue,
  amountPaid,
  stillOwed,
}: Props): JSX.Element | null {
  // Panel 1: "Why is this $0?" — the complaint the inviter would type.
  // Only show when they'd actually ask it: locked value exists, something
  // is unpaid in nominal terms, but net_owed collapsed to zero.
  const locked = Number.parseFloat(lockedValue);
  const paid = Number.parseFloat(amountPaid);
  const whyZero = locked > 0 && paid < locked && stillOwed === 0;
  const [whyZeroOpen, setWhyZeroOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const anyOpen = whyZeroOpen || historyOpen;
  const query = useInviteOwedBreakdown(publicId, { enabled: anyOpen });

  if (!whyZero && !amountPaid) return null;

  const components = query.data?.components ?? [];
  const cashComponents = components.filter((c) => CASH_KINDS.includes(c.kind));
  const explainerComponents = components.filter(
    (c) => !CASH_KINDS.includes(c.kind) || c.kind === 'REVERSAL',
  );

  return (
    <div className="pt-2 space-y-2">
      {whyZero && (
        <details
          className="rounded-md border bg-muted/40 p-2 text-xs"
          open={whyZeroOpen}
          onToggle={(e) => setWhyZeroOpen((e.target as HTMLDetailsElement).open)}
        >
          <summary className="cursor-pointer select-none font-medium">
            Why is this $0?
          </summary>
          <div className="pt-2 space-y-2">
            {query.isLoading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading breakdown…
              </div>
            ) : query.data ? (
              <>
                <p className="text-muted-foreground">{query.data.explanation}</p>
                {explainerComponents.length > 0 ? (
                  <ComponentTable components={explainerComponents} />
                ) : null}
              </>
            ) : null}
          </div>
        </details>
      )}
      <details
        className="rounded-md border bg-muted/40 p-2 text-xs"
        open={historyOpen}
        onToggle={(e) => setHistoryOpen((e.target as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer select-none font-medium">
          Payment history
        </summary>
        <div className="pt-2">
          {query.isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading breakdown…
            </div>
          ) : cashComponents.length === 0 ? (
            <p className="text-muted-foreground">No cash events recorded yet.</p>
          ) : (
            <ComponentTable components={cashComponents} />
          )}
        </div>
      </details>
    </div>
  );
}

function ComponentTable({ components }: { components: BreakdownComponent[] }): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="pb-1 pr-2 text-left font-normal">When</th>
            <th className="pb-1 pr-2 text-left font-normal">Source</th>
            <th className="pb-1 pr-2 text-right font-normal">Amount</th>
            <th className="pb-1 text-left font-normal">Reason</th>
          </tr>
        </thead>
        <tbody>
          {components.map((c) => (
            <tr key={c.eventPublicId} className="border-t">
              <td className="py-1 pr-2 whitespace-nowrap text-muted-foreground">
                {formatDay(c.occurredAt)}
              </td>
              <td className="py-1 pr-2">
                <KindBadge kind={c.kind} />
              </td>
              <td className="py-1 pr-2 text-right tabular-nums">
                {fmtSigned(c.signedAmount)}
              </td>
              <td className="py-1 text-muted-foreground">
                {c.siblingInvitePublicId
                  ? `Duplicate of ${c.siblingInvitePublicId}`
                  : c.payoutPublicId
                  ? `Payout ${c.payoutPublicId}`
                  : c.reason}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
